import {
  collection,
  getCountFromServer,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

/**
 * Shared admin badge counts — singleton + poll (no onSnapshot).
 *
 * Why not onSnapshot: Firestore 12.x hits INTERNAL ASSERTION ca9 when the same
 * watch targets are rapidly subscribed/unsubscribed (React Strict Mode, or
 * AdminLayout + creator sidebar both mounting). Polling avoids the watch stream.
 *
 * Each subscribeAdminBadges() call registers a consumer; only one poller runs
 * for the whole app. Cleanup only stops the poller when the last consumer leaves.
 *
 * pendingSuggestions added below, following the exact same pattern as
 * pendingCommentReports — getCountFromServer on status == "new" (cheaper
 * than the getDocs+filter approach used for support_tickets/commentReports,
 * since suggestions don't need per-priority breakdown the way tickets do).
 */

const EMPTY = {
  pendingCreators: 0,
  waitlistedFans: 0,
  openTickets: 0,
  highPriorityTickets: 0,
  pendingCommentReports: 0,
  pendingSuggestions: 0,
};

const POLL_MS = 20000;

let latest = { ...EMPTY };
const consumers = new Set(); // each entry: { onData, onError }
let pollTimer = null;
let inFlight = null;

async function fetchBadges() {
  let pendingCreators = 0;
  let waitlistedFans = 0;
  let openTickets = 0;
  let highPriorityTickets = 0;
  let pendingCommentReports = 0;
  let pendingSuggestions = 0;

  try {
    const pendingCount = await getCountFromServer(
      query(collection(db, "applications"), where("status", "==", "pending"))
    );
    pendingCreators = pendingCount.data().count;
  } catch (err) {
    console.error("badge applications:", err);
    consumers.forEach((c) => c.onError?.(err));
  }

  try {
    const fansSnap = await getDocs(collection(db, "waitlist_fans"));
    waitlistedFans = fansSnap.docs.filter((d) => d.data().approved !== true).length;
  } catch (err) {
    console.error("badge waitlist_fans:", err);
    consumers.forEach((c) => c.onError?.(err));
  }

  try {
    const ticketsSnap = await getDocs(collection(db, "support_tickets"));
    const open = ticketsSnap.docs.filter((d) => {
      const s = d.data().status;
      return s !== "resolved" && s !== "closed";
    });
    openTickets = open.length;
    highPriorityTickets = open.filter((d) => d.data().priority === "high").length;
  } catch (err) {
    console.error("badge support_tickets:", err);
    consumers.forEach((c) => c.onError?.(err));
  }

  try {
    const reportsSnap = await getDocs(collection(db, "commentReports"));
    pendingCommentReports = reportsSnap.docs.filter(
      (d) => (d.data().status || "pending") === "pending"
    ).length;
  } catch (err) {
    console.error("badge commentReports:", err);
    consumers.forEach((c) => c.onError?.(err));
  }

  try {
    const suggestionsCount = await getCountFromServer(
      query(collection(db, "suggestions"), where("status", "==", "new"))
    );
    pendingSuggestions = suggestionsCount.data().count;
  } catch (err) {
    console.error("badge suggestions:", err);
    consumers.forEach((c) => c.onError?.(err));
  }

  return {
    pendingCreators,
    waitlistedFans,
    openTickets,
    highPriorityTickets,
    pendingCommentReports,
    pendingSuggestions,
  };
}

function emit(data) {
  latest = data;
  consumers.forEach((c) => {
    try {
      c.onData(data);
    } catch (err) {
      console.error("badge consumer error:", err);
    }
  });
}

async function tick() {
  if (inFlight) return inFlight;
  inFlight = fetchBadges()
    .then((data) => {
      if (consumers.size > 0) emit(data);
      return data;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/** Force an immediate badge refresh (e.g. on admin route change). */
export function refreshAdminBadges() {
  return tick();
}

function startPoller() {
  if (pollTimer != null) return;
  tick();
  pollTimer = setInterval(tick, POLL_MS);
}

function stopPoller() {
  if (pollTimer != null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  inFlight = null;
}

/**
 * @returns {() => void} unsubscribe — safe to call multiple times
 */
export function subscribeAdminBadges(onData, onError) {
  const consumer = { onData, onError };
  consumers.add(consumer);
  try {
    onData(latest);
  } catch (_) {
    /* ignore */
  }
  startPoller();
  let cleaned = false;
  return () => {
    if (cleaned) return;
    cleaned = true;
    consumers.delete(consumer);
    if (consumers.size === 0) {
      stopPoller();
    }
  };
}
